import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { MePartner, UserPermissions } from 'src/app/models/user-permissions.interface';
import { PermissionAction } from 'src/app/models/permission-action.interface';
import { PermissionPageLockedReason } from 'src/app/models/permission-page.interface';
import {
  RawScopeType,
  ScopeType,
  UserRole,
} from 'src/app/models/user-role.interface';
import { MENU_CONFIG, MenuItem } from './menu-config';

/**
 * Menú de admin del usuario de partner: su `/me` llega con `pages` vacío, así
 * que no se filtra por páginas, sino que ve solo sus empresas y sus solicitudes
 * de grandes cuentas (las mismas entradas del menú de WAIIS, con sus datos).
 */
const PARTNER_MENU_IDS = ['corporations_section_title', 'corporations', 'corporations_partner_requests'];

/** Entrada «Solicitudes», que lleva el contador de solicitudes nuevas del partner. */
const PARTNER_REQUESTS_MENU_ID = 'corporations_partner_requests';

/** «Mi partner»: lo que gestiona el propio partner (de momento, sus usuarios). */
const PARTNER_OWN_MENU: MenuItem[] = [
  { id: 'my_partner_section_title', name: 'SIDEBAR.MY_PARTNER', section_title: true },
  {
    id: 'partner_users',
    name: 'SIDEBAR.PARTNER_USERS',
    icon: { type: 'svg', name: 'users' },
    routerLink: ['/admin/partner-users'],
  },
];

/** «Mi partner › Condiciones de pago»: solo con `/me.partner.payment_terms_enabled`. */
const PARTNER_PAYMENTS_MENU: MenuItem = {
  id: 'partner_payments',
  name: 'SIDEBAR.PARTNER_PAYMENTS',
  icon: { type: 'svg', name: 'credit-card' },
  routerLink: ['/admin/partner-payments'],
};

@Injectable({ providedIn: 'root' })
export class PermissionsService {
  private permissionsSubject = new BehaviorSubject<UserPermissions | null>(
    null,
  );
  loadPermissionsError$ = new BehaviorSubject<Error | null>(null);

  /**
   * Empresa que se está viendo ahora mismo (`/corporation/:corporationId/...`).
   * Un usuario puede tener roles en VARIAS empresas a la vez (p.ej.
   * `corporation_admin` de una y `corporation_office_admin` de otra distinta)
   * — sin esto, `canViewAllOffices()`/`getAllowedOfficeIds()` miraban "¿tiene
   * ESTE rol en CUALQUIER empresa?", dando visión total sobre la empresa A a
   * alguien que solo administra la empresa B. La fija `CorporationActiveGuard`,
   * que corre en todas las rutas de empresa antes que cualquier otro guard o
   * componente.
   */
  private currentCorporationId: string | null = null;

  setCurrentCorporationId(corporationId: string | null): void {
    this.currentCorporationId = corporationId;
  }

  getPermissions() {
    return this.permissionsSubject.asObservable();
  }

  get currentPermissions(): UserPermissions | null {
    return this.permissionsSubject.value;
  }

  /** Partner que administra el usuario, o `null` si no es usuario de partner. */
  get partner(): MePartner | null {
    return this.permissionsSubject.value?.partner ?? null;
  }

  /**
   * «Mi partner»: el equipo (no para el subtutor, que es de solo lectura y
   * recibe 403) y las condiciones de pago si el partner las tiene. El título
   * solo va si queda alguna entrada debajo.
   */
  private partnerOwnMenu(): MenuItem[] {
    const [title, ...team] = PARTNER_OWN_MENU;
    const items = [
      ...(this.partner?.read_only ? [] : team),
      ...(this.partner?.payment_terms_enabled === true ? [PARTNER_PAYMENTS_MENU] : []),
    ];
    return items.length ? [title, ...items] : [];
  }

  /** Usuario de un partner: entra al admin, pero solo con sus empresas. */
  isPartnerUser(): boolean {
    return this.partner !== null;
  }

  /** Solicitudes nuevas del partner: el contador de «Solicitudes» (sin contador con 0). */
  private partnerNewRequests = 0;
  readonly partnerNewRequests$ = new BehaviorSubject<number>(0);

  setPartnerNewRequests(count: number): void {
    this.partnerNewRequests = count;
    this.partnerNewRequests$.next(count);
  }

  setPermissions(perms: UserPermissions): void {
    this.loadPermissionsError$.next(null);
    this.permissionsSubject.next({ ...perms, timestamp: Date.now() });
  }

  clearPermissions(): void {
    this.permissionsSubject.next(null);
    this.loadPermissionsError$.next(null);
  }

  isSuperAdmin(): boolean {
    const perms = this.permissionsSubject.value;
    if (!perms) return false;
    return perms.roles.some((role) => this.isSuperAdminRole(role));
  }

  hasPageAccess(pageName: string): boolean {
    if (this.isSuperAdmin()) return true;
    const perms = this.permissionsSubject.value;
    if (!perms) return false;
    return perms.pages.some((p) => p.name === pageName);
  }

  /**
   * Motivo por el que `/me` marca una pantalla como bloqueada, o `null` si es
   * accesible (o si no hay permisos cargados todavía).
   *
   * Una pantalla bloqueada SIGUE apareciendo en `pages[]`: es candado, no
   * ausencia. Un superadmin no pasa por ninguno de los filtros.
   */
  getPageLockedReason(pageName: string): PermissionPageLockedReason | null {
    if (this.isSuperAdmin()) return null;
    const page = this.permissionsSubject.value?.pages.find((p) => p.name === pageName);
    if (!page?.locked) return null;
    return page.locked_reason ?? null;
  }

  hasAction(actionName: string): boolean {
    if (this.isSuperAdmin()) return true;
    const perms = this.permissionsSubject.value;
    if (!perms) return false;
    return perms.actions.some((a) => a.name === actionName);
  }

  getActionById(actionId: string): PermissionAction | undefined {
    const perms = this.permissionsSubject.value;
    if (!perms) return undefined;
    return perms.actions.find((a) => a.id === actionId);
  }

  /** Returns the corporationId if the user has a corporation_admin role (not superadmin). */
  getCorporationAdminId(): string | null {
    if (this.isSuperAdmin()) return null;
    const perms = this.permissionsSubject.value;
    if (!perms) return null;
    const role = perms.roles.find((r) => this.isCorporationAdminRole(r));
    return role?.scope_id ?? null;
  }

  /**
   * True when the user's access is limited to one or more specific corporation
   * offices — i.e. they hold at least one `corporation_office` role and are
   * neither a superadmin nor a corporation-wide admin.
   */
  isOfficeScoped(): boolean {
    if (this.canViewAllOffices()) return false;
    const perms = this.permissionsSubject.value;
    if (!perms) return false;
    return perms.roles.some((r) => this.isOfficeRole(r));
  }

  /**
   * True when the user may see every office of the corporation (superadmin or
   * corporation-wide admin). Used to decide whether an "all offices" aggregate
   * option should be offered.
   */
  canViewAllOffices(): boolean {
    if (this.isSuperAdmin()) return true;
    // El usuario de partner ve sus empresas enteras; el backend ya le niega
    // las que no son de su partner.
    if (this.isPartnerUser()) return true;
    const perms = this.permissionsSubject.value;
    if (!perms) return false;
    return perms.roles.some((r) => this.isCorporationAdminRole(r));
  }

  /**
   * Office ids the user is allowed to see, derived from their
   * `corporation_office` roles.
   *
   * Returns an empty array for global scopes (superadmin / corporation admin),
   * which by convention means "no office restriction — all offices".
   */
  getAllowedOfficeIds(): string[] {
    if (this.canViewAllOffices()) return [];
    const perms = this.permissionsSubject.value;
    if (!perms) return [];
    const ids = perms.roles
      .filter((r) => this.isOfficeRole(r))
      .map((r) => r.scope_id)
      .filter((id): id is string => !!id);
    return Array.from(new Set(ids));
  }

  /**
   * Filters an arbitrary list of offices down to the ones the user may see.
   * Global-scope users get the list back unchanged.
   */
  filterAllowedOffices<T extends { id: string }>(offices: T[]): T[] {
    const allowed = this.getAllowedOfficeIds();
    if (allowed.length === 0) return offices;
    const allowedSet = new Set(allowed);
    return offices.filter((o) => allowedSet.has(o.id));
  }

  /**
   * Resolves the office a view should be pinned to, validating any requested id
   * (typically read from a URL) against the user's real scope. A requested id
   * that is not allowed is ignored — it never leaks another office's data.
   *
   * - Global scope: honors the request as-is (`null` means "all offices").
   * - Scoped user: returns the requested id when it is one of their allowed
   *   offices, otherwise their first allowed office. It never returns `null`
   *   for a scoped user, because the backend treats a missing office filter as
   *   "all offices of the corporation" — which would leak data. This keeps the
   *   restriction in place even when no id is requested (missing/removed query
   *   param) and lets multi-office users switch only among their own offices.
   */
  resolveOfficeId(requestedOfficeId?: string | null): string | null {
    const allowed = this.getAllowedOfficeIds();
    if (allowed.length === 0) return requestedOfficeId ?? null;
    if (requestedOfficeId && allowed.includes(requestedOfficeId)) {
      return requestedOfficeId;
    }
    return allowed[0];
  }

  /** Whether the given office id is within the user's allowed scope. */
  isOfficeAllowed(officeId: string | null | undefined): boolean {
    if (!officeId) return false;
    const allowed = this.getAllowedOfficeIds();
    return allowed.length === 0 || allowed.includes(officeId);
  }

  /**
   * Normalizes the raw `scope_type` from the API into its canonical form,
   * absorbing the legacy `coporation` misspelling so no caller has to match
   * the typo by hand.
   */
  private normalizeScopeType(scopeType: RawScopeType | null | undefined): '' | ScopeType {
    const value = (scopeType ?? '').toLowerCase();
    if (value === 'coporation' || value === 'corporation') return 'corporation';
    if (value === 'corporation_office') return 'corporation_office';
    if (value === 'fleet') return 'fleet';
    return '';
  }

  /** Roles de flota de taxi/VTC del usuario (`scope_type = fleet`), uno por flota. */
  getFleetRoles(): UserRole[] {
    const perms = this.permissionsSubject.value;
    if (!perms) return [];
    return perms.roles.filter(
      (role) => this.normalizeScopeType(role.scope_type) === 'fleet' && !!role.scope_id,
    );
  }

  /** Rol del usuario en una flota concreta, o `null` si no pertenece a ella. */
  getFleetRole(fleetId: string): UserRole | null {
    return this.getFleetRoles().find((role) => role.scope_id === fleetId) ?? null;
  }

  private isSuperAdminRole(role: UserRole): boolean {
    return (
      role.role_name === 'superadmin' || role.permission_name === 'superadmin'
    );
  }

  private isCorporationAdminRole(role: UserRole): boolean {
    // Comparación EXACTA, no por subcadena: los roles de admin de empresa son
    // `corporation_admin` y, en empresas con partner, su versión tutelada
    // `corporation_admin_tutored` (WS-2259; sus secciones las limita
    // `PartnerSectionAccessService`). Un `includes('corporation')` colaba
    // cualquier otro rol con ámbito `corporation` en su nombre (p.ej. uno de
    // onboarding tipo `create_corporation`) como si fuera admin de toda la
    // empresa, dándole `canViewAllOffices()` sin deberlo tener. Sin el tutelado,
    // el admin de una empresa con partner no tenía ni empresa ni sede y
    // `OfficeFilterGuard` le sacaba al tablero.
    const name = (role.role_name || role.permission_name || '').toLowerCase();
    if (name !== 'corporation_admin' && name !== 'corporation_admin_tutored') return false;
    if (this.normalizeScopeType(role.scope_type) !== 'corporation') return false;
    if (!role.scope_id) return false;
    // Si se conoce la empresa actual, el rol debe ser DE ESA empresa: ser
    // corporation_admin de la empresa B no da visión total sobre la empresa A.
    if (this.currentCorporationId && role.scope_id !== this.currentCorporationId) return false;
    return true;
  }

  private isOfficeRole(role: UserRole): boolean {
    if (this.normalizeScopeType(role.scope_type) !== 'corporation_office') return false;
    if (!role.scope_id) return false;
    // Mismo motivo que en isCorporationAdminRole: una sede de la empresa B no
    // debe contar como "sede permitida" al mirar la empresa A.
    if (this.currentCorporationId && role.scope?.corporation_id !== this.currentCorporationId) return false;
    return true;
  }

  getVisibleMenuItems(): MenuItem[] {
    const perms = this.permissionsSubject.value;
    console.log(
      '[PermissionsService] getVisibleMenuItems called. Current perms:',
      perms,
    );

    if (!perms) {
      console.log(
        '[PermissionsService] No perms available, returning empty array',
      );
      return [];
    }

    if (this.isPartnerUser()) {
      return [
        ...MENU_CONFIG.filter((item) => PARTNER_MENU_IDS.includes(item.id ?? '')).map((item) =>
          item.id === PARTNER_REQUESTS_MENU_ID && this.partnerNewRequests > 0
            ? { ...item, count: this.partnerNewRequests, countTitle: 'SIDEBAR.PARTNER_REQUESTS_NEW' }
            : item,
        ),
        ...this.partnerOwnMenu(),
      ];
    }

    // Si es superadmin, mostrar todo el menú sin filtrar
    if (this.isSuperAdmin()) {
      console.log(
        'Superadmin detected - returning full MENU_CONFIG with',
        MENU_CONFIG.length,
        'items',
      );
      return MENU_CONFIG;
    }

    console.log('Not superadmin - filtering menu');

    const isVisible = (item: MenuItem): boolean => {
      if (!item.permission_page) return true;
      return this.hasPageAccess(item.permission_page);
    };

    const filterItems = (items: MenuItem[]): MenuItem[] => {
      const result: MenuItem[] = [];

      for (const item of items) {
        if (item.section_title) {
          result.push(item);
          continue;
        }

        if (item.submenus && item.submenus.length > 0) {
          const visibleSubs = item.submenus
            .map((sub) => {
              if (sub.submenus && sub.submenus.length > 0) {
                const visibleNested = sub.submenus.filter((n) => isVisible(n));
                if (!isVisible(sub) && visibleNested.length === 0) return null;
                return { ...sub, submenus: visibleNested };
              }
              return isVisible(sub) ? sub : null;
            })
            .filter((s): s is MenuItem => s !== null);

          if (isVisible(item) && visibleSubs.length > 0) {
            result.push({ ...item, submenus: visibleSubs });
          }
        } else {
          if (isVisible(item)) {
            result.push(item);
          }
        }
      }

      // Remove section titles with no visible item of their own.
      // Ojo: hay que mirar sólo hasta el SIGUIENTE título de sección. Antes se
      // comprobaba `result.slice(i + 1)` completo, así que a una sección con todos
      // sus hijos filtrados le bastaba con que existiera cualquier item más adelante
      // en el menú para que su cabecera se quedara huérfana en el sidebar.
      return result.filter((item, i) => {
        if (!item.section_title) return true;

        const nextTitle = result.findIndex((n, j) => j > i && n.section_title);
        const ownItems =
          nextTitle === -1
            ? result.slice(i + 1)
            : result.slice(i + 1, nextTitle);

        return ownItems.some((n) => !n.section_title);
      });
    };

    return filterItems(MENU_CONFIG);
  }
}
